import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef(null);

  const services = [
    { key: "smartHomeAutomation", path: "/services/smart-home" },
    { key: "securityAndSurveillanceSystem", path: "/services/security" },
    { key: "intercomSystem", path: "/services/intercom" },
    { key: "smartLockAndAccessControlSystems", path: "/services/smart-lock" },
    { key: "soundSystems", path: "/services/sound" },
    { key: "lighteningSolutions", path: "/services/lightening" },
    { key: "electricalWiringAccessories", path: "/services/electrical" }
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle mouse enter for dropdown
  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setServicesOpen(false);
    }, 300);
  };

  // Clear timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-md' : 'backdrop-blur-sm'
        }`}
      style={{
        background: scrolled ? 'rgba(45, 45, 43, 0.95)' : 'rgba(45, 45, 43, 0.98)',
        boxShadow: scrolled ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.2)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <a href="https://www.liwansolutions.com/" className="flex items-center group">
            <img
              src="/LiwanLogo1.webp"
              alt="Logo"
              className="h-14 w-auto transition-transform group-hover:scale-105 duration-300 object-contain"
            />
          </a>

          {/* Desktop Navigation - Only show on large screens */}
          <div className="hidden lg:flex items-center space-x-2">
            <Link
              to="/"
              className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("home")}</span>
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: '#ffde21' }}
              />
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group flex items-center gap-2"
                style={{ color: 'white' }}
              >
                <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("services")}</span>
                <ChevronDown
                  size={18}
                  className={`relative z-10 transition-transform duration-300 text-white group-hover:text-[#2d2d2b] ${servicesOpen ? 'rotate-180' : ''}`}
                />
                <div
                  className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: '#ffde21' }}
                />
              </button>

              {/* Dropdown Menu */}
              {servicesOpen && (
                <div
                  className="absolute top-full left-0 pt-3 w-80"
                >
                  {/* Invisible bridge */}
                  <div className="h-3 -mt-3" />

                  <div
                    className="rounded-2xl overflow-hidden backdrop-blur-xl"
                    style={{
                      background: 'rgba(45, 45, 43, 0.98)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                      border: '2px solid rgba(255, 222, 33, 0.3)'
                    }}
                  >
                    <div className="p-2">
                      {services.map((service) => (
                        <Link
                          key={service.key}
                          to={service.path}
                          className="relative block px-5 py-3.5 rounded-xl font-medium transition-all duration-200 group overflow-hidden"
                          style={{ color: 'white' }}
                        >
                          <span className="relative z-10 group-hover:translate-x-2 inline-block transition-transform duration-200 text-white group-hover:text-[#2d2d2b]">
                            {t(service.key)}
                          </span>
                          <div
                            className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            style={{ background: '#ffde21' }}
                          />
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://liwansolutions.odoo.com/shop"
              className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("shop")}</span>
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: '#ffde21' }}
              />
            </a>

            <Link
              to="/supplier"
              className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("become_supplier")}</span>
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: '#ffde21' }}
              />
            </Link>

            <Link
              to="/contact"
              className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("contact.title")}</span>
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: '#ffde21' }}
              />
            </Link>

            <Link
              to="/design-home"
              className="relative px-5 py-2.5 rounded-lg font-semibold text-base transition-all duration-300 overflow-hidden group"
              style={{ color: '#2d2d2b', background: '#ffde21' }}
            >
              <span className="relative z-10">{i18n.dir() === "rtl" ? "صمم منزلك" : "Design Your Home"}</span>
            </Link>

            <div className="ml-2">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Menu Button - Only show on small screens */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-3 rounded-xl transition-all duration-300 relative group"
            style={{ color: 'white' }}
          >
            <div
              className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: '#ffde21' }}
            />
            <div className="relative z-10">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </div>
          </button>
        </div>

        {/* Mobile Menu - Only show on small screens when menu is open */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-screen opacity-100 pb-6' : 'max-h-0 opacity-0'
            }`}
        >
          <div className="flex flex-col space-y-2 pt-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="relative px-5 py-3.5 rounded-xl font-semibold transition-all duration-200 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("home")}</span>
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ background: '#ffde21' }}
              />
            </Link>

            {/* Mobile Services Accordion */}
            <div className="rounded-xl overflow-hidden">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="w-full px-5 py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-between"
                style={{
                  color: servicesOpen ? '#2d2d2b' : 'white',
                  background: servicesOpen ? '#ffde21' : 'transparent'
                }}
              >
                <span>{t("services")}</span>
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${servicesOpen ? 'max-h-96 mt-2' : 'max-h-0'
                  }`}
              >
                <div className="space-y-1 ml-4">
                  {services.map((service) => (
                    <Link
                      key={service.key}
                      to={service.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="relative block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 overflow-hidden group"
                      style={{ color: 'white' }}
                    >
                      <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t(service.key)}</span>
                      <div
                        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        style={{ background: 'rgba(255, 222, 33, 0.3)' }}
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="https://liwansolutions.odoo.com/shop"
              className="relative px-5 py-3.5 rounded-xl font-semibold transition-all duration-200 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("shop")}</span>
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ background: '#ffde21' }}
              />
            </a>

            <Link
              to="/supplier"
              onClick={() => setMobileMenuOpen(false)}
              className="relative px-5 py-3.5 rounded-xl font-semibold transition-all duration-200 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("become_supplier")}</span>
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ background: '#ffde21' }}
              />
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="relative px-5 py-3.5 rounded-xl font-semibold transition-all duration-200 overflow-hidden group"
              style={{ color: 'white' }}
            >
              <span className="relative z-10 text-white group-hover:text-[#2d2d2b]">{t("contact.title")}</span>
              <div
                className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                style={{ background: '#ffde21' }}
              />
            </Link>

            <Link
              to="/design-home"
              onClick={() => setMobileMenuOpen(false)}
              className="mx-5 py-3.5 rounded-xl font-semibold text-center transition-all duration-200"
              style={{ color: '#2d2d2b', background: '#ffde21' }}
            >
              {i18n.dir() === "rtl" ? "صمم منزلك" : "Design Your Home"}
            </Link>

            <div className="pt-4">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
